<template>
  <div class="md:pt-16 md:px-32 px-6 pt-8 bg-gray-100 border-t">
    <div class="container flex flex-wrap justify-between">
      <div class="md:w-1/4 w-full">
        <router-link :to="{ name: 'home' }" title="AI cycling analytics">
          <Logo class="w-32 mb-4" />
        </router-link>
        <p class="md:block text-muted hidden">Intuitive, science-backed training analytics for cyclists, runners, and swimmers.</p>
        <div class="text-muted mt-6 mb-8">
          <a href="https://www.instagram.com/formbeat/" target="_blank" title="Follow us on Instagram">
            <svg class="svg-icon svg-icon--small">
              <use xlink:href="#icon-instagram"></use>
            </svg>
          </a>
          <a href="https://www.facebook.com/formbeat" target="_blank" title="Follow us on Facebook" class="ml-2">
            <svg class="svg-icon svg-icon--small">
              <use xlink:href="#icon-facebook"></use>
            </svg>
          </a>
          <a href="https://twitter.com/formbeatcom" target="_blank" title="Follow us on Twitter" class="ml-2">
            <svg class="svg-icon svg-icon--small">
              <use xlink:href="#icon-twitter"></use>
            </svg>
          </a>
        </div>
        <div class="flex my-6 space-x-4">
          <a href="https://apps.apple.com/us/app/formbeat/id1539444595" target="_blank">
            <img src="~/images/icons/download-app-store-light.svg" alt="Cycling training app for iOS" class="object-contain w-auto h-12" />
          </a>
          <a href="https://play.google.com/store/apps/details?id=com.formbeat.www" target="_blank">
            <img src="~/images/icons/download-google-play-light.svg" alt="Cycling training app for Android" class="object-contain w-auto h-12" />
          </a>
        </div>
      </div>

      <div class="sm:w-3/4 md:w-2/4 w-full text-sm">
        <div class="md:justify-center gap-x-6 gap-y-8 md:flex md:flex-wrap md:gap-x-12 grid grid-cols-2">
          <div class="md:flex-shrink-0">
            <div class="label">About</div>
            <nav>
              <div class="inline-block py-1">
                <router-link :to="{ name: 'signIn' }" title="Log in to your account">Sign In</router-link>
                /
                <router-link :to="{ name: 'join' }" title="Sign Up">Sign Up</router-link>
              </div>
              <br />
              <router-link :to="{ name: 'home', query: { section: 'features' } }" class="inline-block py-1" title="Features">Features</router-link>
              <br />
              <router-link :to="{ name: 'pricing' }" class="inline-block py-1" title="Pricing">Pricing</router-link>
              <br />
              <router-link :to="{ name: 'ambassadors' }" class="inline-block py-1" title="FormBeat Ambassador Program">Ambassadors</router-link>
              <br />
              <router-link :to="{ name: 'mobile' }" class="inline-block py-1" title="Download cycling training apps">Mobile App</router-link>
              <br />
              <router-link :to="{ name: 'cyclingTrainingPlans' }" class="inline-block py-1" title="Adaptive Cycling Training Plans">Training Plans</router-link>
              <br />
              <router-link :to="{ name: 'aiCyclingCoach' }" class="inline-block py-1" title="AI Cycling Coach">AI Coach</router-link>
              <br />
              <router-link :to="{ name: 'todaysPlan' }" class="inline-block py-1" title="Today's Plan Alternative">Today's Plan Alternative</router-link>
              <br />
              <router-link :to="{ name: 'trainingPeaksAlternative' }" class="inline-block py-1" title="TrainingPeaks Alternative">
                TrainingPeaks Alternative
              </router-link>
              <br />
              <router-link :to="{ name: 'trainerRoadAlternative' }" class="inline-block py-1" title="TrainerRoad Alternative">
                TrainerRoad Alternative
              </router-link>
              <br />
              <router-link :to="{ name: 'stravaAlternative' }" class="inline-block py-1" title="Strava Alternative">Strava Alternative</router-link>
              <br />
              <router-link :to="{ name: 'intervalsIcuAlternative' }" class="inline-block py-1" title="intervals.icu Alternative">
                intervals.icu Alternative
              </router-link>
            </nav>
          </div>

          <div class="md:flex-shrink-0">
            <div class="label">Get Help</div>
            <nav>
              <router-link class="inline-block py-1" :to="{ name: 'help' }" title="Get help">Help & Support</router-link>
              <br />
              <router-link class="inline-block py-1" :to="{ name: 'training-glossary' }" title="Training Glossary">Training Glossary</router-link>
              <br />
              <router-link class="inline-block py-1" :to="{ name: 'tools' }" title="Cycling Tools and Calculators">Tools</router-link>
              <br />
              <router-link class="inline-block py-1" :to="{ name: 'changelog' }" title="Discover features recently added to FormBeat cycling Analytics">
                What's new?
              </router-link>
            </nav>
          </div>

          <div class="md:flex-shrink-0">
            <div class="label">Tools &amp; Calculators</div>
            <nav>
              <router-link class="inline-block py-1" :to="{ name: 'tools.ftp' }" title="Cycling FTP Power Calculator">FTP Calculator</router-link>
              <br />
              <router-link class="inline-block py-1" :to="{ name: 'cyclingWorkouts' }" title="Structured Cycling Workouts by Goal">
                Cycling Workouts
              </router-link>
              <br />
              <router-link class="inline-block py-1" :to="{ name: 'tools.workoutBuilder' }" title="Free Cycling Workout Builder">Workout Builder</router-link>
              <br />
              <router-link class="inline-block py-1" :to="{ name: 'tools.heartRateZones' }" title="Cycling Heart Rate Zones Calculator">
                HR Zones Calculator
              </router-link>
              <br />
              <router-link class="inline-block py-1" :to="{ name: 'tools.powerZones' }" title="Cycling Power Training Zones Calculator">
                Power Zones Calculator
              </router-link>
              <br />
              <router-link class="inline-block py-1" :to="{ name: 'tools.tss' }" title="Cycling TSS Calculator">TSS Calculator</router-link>
              <br />
              <router-link class="inline-block py-1" :to="{ name: 'tools.zone2Training' }" title="Zone 2 Training Calculator">Zone 2 Calculator</router-link>
              <br />
              <router-link class="inline-block py-1" :to="{ name: 'tools' }" title="All Cycling Calculators">All Calculators</router-link>
            </nav>
          </div>
        </div>
      </div>

      <div class="lg:flex lg:w-1/4 flex-col items-center hidden text-center">
        <div>
          <a
            href="https://ecologi.com/formbeat?r=61b9ebfdd3c429b59ed3949a"
            target="_blank"
            rel="noopener noreferrer"
            title="View our Ecologi profile"
            style="width: 200px; display: inline-block"
          >
            <img
              alt="We offset our carbon footprint via Ecologi"
              src="https://ecologi-assets.imgix.net/badges/climate-positive-workforce-black-landscape.png"
              style="width: 184px"
            />
          </a>
        </div>
      </div>
    </div>

    <hr class="mt-8" />

    <div class="text-muted flex flex-wrap items-center justify-between py-6 text-xs">
      <div class="md:w-1/3 w-full">
        &copy; {{ new Date().getFullYear() }}, FormBeat.
        <router-link class="inline-block py-1" :to="{ name: 'terms' }" title="Terms of Use">Terms of Use</router-link>
        ,
        <router-link class="inline-block py-1" :to="{ name: 'privacy' }" title="Privacy Policy">Privacy Policy</router-link>
        ,
        <button class="inline-block py-1 underline" title="Cookie settings" @click="openCookieSettings">Cookie settings</button>
      </div>
      <div class="md:block md:text-center hidden">
        <img src="~images/strava-badge-alt.svg" alt="" class="w-24 mx-auto" />
      </div>
      <div class="md:w-1/3 md:text-right w-full">
        <div>
          AI powered by
          <a class="underline" target="_blank" href="https://www.getvoila.ai" title="Voilà - AI powered ChatGPT browser assistant">Voilà</a>
        </div>
        <div>
          NP®, IF® and TSS® are trademarks of
          <a class="underline" rel="nofollow" target="_blank" href="https://www.trainingpeaks.com">Peaksware, LLC</a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import bus from 'bus'

export default {
  methods: {
    openCookieSettings() {
      bus.$emit('consent:open')
    }
  }
}
</script>
